import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaPlus, FaSearch, FaFilter, FaEdit, FaTrash, FaEye, FaMusic } from 'react-icons/fa';
import axios from 'axios';
import '../styles/DJBookings.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function DJBookingsList() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');

  useEffect(() => {
    loadBookings();
  }, []);

  const loadBookings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/dj`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(response.data.bookings || []);
    } catch (error) {
      console.error('Error loading DJ bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('DJ-Buchung wirklich löschen?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/dj/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(bookings.filter(b => b.id !== id));
    } catch (error) {
      alert('Fehler beim Löschen: ' + error.message);
    }
  };

  // Filter & Sort
  const filteredBookings = bookings
    .filter(booking => {
      const matchesSearch = 
        booking.Nachname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.Vorname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking['E-Mail']?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking['Event Ort']?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || booking.Buchungsstatus === statusFilter;
      
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      switch(sortBy) {
        case 'date':
          return new Date(b['Eingegangen am']) - new Date(a['Eingegangen am']);
        case 'name':
          return (a.Nachname || '').localeCompare(b.Nachname || '');
        case 'event':
          return new Date(a['Event-Datum']) - new Date(b['Event-Datum']);
        default:
          return 0;
      }
    });

  const getStatusColor = (status) => {
    const statusMap = {
      'Neu': 'neu',
      'In Bearbeitung': 'angefragt',
      'Angebot erstellt': 'angefragt',
      'Bestätigt': 'bestaetigt',
      'Abgeschlossen': 'abgeschlossen',
      'Abgelehnt': 'zurueck'
    };
    return statusMap[status] || 'neu';
  };

  if (loading) {
    return <div className="spinner"></div>;
  }

  return (
    <div className="dj-bookings-page">
      <div className="page-header">
        <div>
          <h1><FaMusic /> DJ buchen</h1>
          <p className="text-gray">Alle DJ-Anfragen und Buchungen im Überblick</p>
        </div>
        <Link to="/dj/new" className="btn btn-primary">
          <FaPlus /> Neue DJ-Buchung
        </Link>
      </div>

      {/* Filters */}
      <div className="filters-bar card">
        <div className="search-box">
          <FaSearch />
          <input
            type="text"
            placeholder="Suche nach Name, E-Mail, Ort..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <FaFilter />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">Alle Status</option>
            <option value="Neu">Neu</option>
            <option value="In Bearbeitung">In Bearbeitung</option>
            <option value="Angebot erstellt">Angebot erstellt</option>
            <option value="Bestätigt">Bestätigt</option>
            <option value="Abgeschlossen">Abgeschlossen</option>
            <option value="Abgelehnt">Abgelehnt</option>
          </select>
        </div>

        <div className="filter-group">
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="date">Sortieren: Eingangsdatum</option>
            <option value="event">Sortieren: Event-Datum</option>
            <option value="name">Sortieren: Name</option>
          </select>
        </div>

        <div className="results-count">
          {filteredBookings.length} DJ-Buchungen
        </div>
      </div>

      {/* Bookings Table */}
      {filteredBookings.length === 0 ? (
        <div className="card text-center" style={{ padding: '3rem' }}>
          <FaMusic style={{ fontSize: '3rem', color: '#8b5cf6', marginBottom: '1rem' }} />
          <p className="text-gray">Noch keine DJ-Buchungen vorhanden.</p>
        </div>
      ) : (
        <div className="card">
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Kunde</th>
                  <th>E-Mail</th>
                  <th>Event-Typ</th>
                  <th>Event-Datum</th>
                  <th>Event-Ort</th>
                  <th>Gäste</th>
                  <th>Preis</th>
                  <th>Status</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {filteredBookings.map(booking => (
                  <tr key={booking.id}>
                    <td>
                      <strong>{booking.Vorname} {booking.Nachname}</strong>
                    </td>
                    <td>{booking['E-Mail']}</td>
                    <td>{booking['Event-Typ']?.[0] || '-'}</td>
                    <td>
                      {booking['Event-Datum'] ? 
                        new Date(booking['Event-Datum']).toLocaleDateString('de-DE') : 
                        '-'
                      }
                    </td>
                    <td>{booking['Event Ort']}</td>
                    <td>{booking.Gästeanzahl || '-'}</td>
                    <td className="text-primary">
                      <strong>
                        {booking['Manueller Preis aus CRM'] 
                          ? `${booking['Manueller Preis aus CRM']}€` 
                          : (booking.Gesamtpreis ? `${booking.Gesamtpreis}€` : '-')}
                      </strong>
                    </td>
                    <td>
                      <span className={`status-badge status-${getStatusColor(booking.Buchungsstatus)}`}>
                        {booking.Buchungsstatus || 'Neu'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <Link 
                          to={`/dj/${booking.id}`} 
                          className="btn-icon"
                          title="Details"
                        >
                          <FaEye />
                        </Link>
                        <Link 
                          to={`/dj/${booking.id}/edit`} 
                          className="btn-icon"
                          title="Bearbeiten"
                        >
                          <FaEdit />
                        </Link>
                        <button 
                          onClick={() => handleDelete(booking.id)}
                          className="btn-icon btn-danger"
                          title="Löschen"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default DJBookingsList;
